import React, { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Search, Pencil, Trash2, ChevronUp, ChevronDown, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import CAPIFormModal from '../../components/admin/CAPIFormModal';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';

interface CAPIConfig {
  id?: number;
  pixel_id: string;
  name: string;
  access_token: string;
  access_token_masked?: string;
  test_event_code: string;
  is_active: boolean;
  send_page_view_events?: boolean;
  send_add_to_cart_events: boolean;
  send_purchase_events: boolean;
  send_initiate_checkout_events: boolean;
  send_view_content_events: boolean;
  send_lead_events: boolean;
  created_at?: string;
  updated_at?: string;
}

const FacebookCAPIManagement: React.FC = () => {
  const [configs, setConfigs] = useState<CAPIConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<CAPIConfig | null>(null);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success'|'error'}>({ show: false, message: '', type: 'success' });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFacebookCAPI();
      let data = response.data || response;
      if (data?.results && Array.isArray(data.results)) data = data.results;
      setConfigs(Array.isArray(data) ? data : data ? [data] : []);
    } catch (error) { console.error('Failed:', error); setConfigs([]); }
    finally { setLoading(false); }
  };

  const handleAdd = () => { setSelectedConfig(null); setIsModalOpen(true); };
  const handleEdit = (c: CAPIConfig) => { setSelectedConfig(c); setIsModalOpen(true); };
  const handleDeleteClick = (c: CAPIConfig) => { setSelectedConfig(c); setIsDeleteDialogOpen(true); };

  const handleDelete = async () => {
    if (!selectedConfig) return;
    try {
      await adminAPI.deleteFacebookCAPI(selectedConfig.id!);
      setToast({ show: true, message: 'Config deleted', type: 'success' });
      loadConfigs();
    } catch { setToast({ show: true, message: 'Failed to delete', type: 'error' }); }
    finally { setIsDeleteDialogOpen(false); setSelectedConfig(null); }
  };

  const handleTestConnection = async (c: CAPIConfig) => {
    try {
      setTestingConnection(c.id!);
      const response = await adminAPI.testFacebookCAPI(c.id!);
      const data = response.data || response;
      setToast({ show: true, message: data.message || 'Test successful', type: data.status === 'success' ? 'success' : 'error' });
    } catch (error: any) {
      setToast({ show: true, message: error.response?.data?.message || 'Test failed', type: 'error' });
    } finally { setTestingConnection(null); }
  };

  const handleSave = async (data: any) => {
    try {
      if (selectedConfig?.id) { await adminAPI.updateFacebookCAPI(selectedConfig.id, data); setToast({ show: true, message: 'Config updated', type: 'success' }); }
      else { await adminAPI.createFacebookCAPI(data); setToast({ show: true, message: 'Config created', type: 'success' }); }
      setIsModalOpen(false); setSelectedConfig(null); loadConfigs();
    } catch (error: any) {
      setToast({ show: true, message: error.response?.data?.message || 'Failed to save', type: 'error' });
    }
  };

  const handleSort = (f: string) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } setCurrentPage(1); };
  const SA = ({ field }: { field: string }) => sortField !== field ? null : sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;

  const filtered = useMemo(() => {
    let list = configs.filter(c => [c.pixel_id, c.name].some(v => v?.toLowerCase().includes(searchTerm.toLowerCase())));
    list = [...list].sort((a: any, b: any) => sortDir === 'asc' ? String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? '')) : String(b[sortField] ?? '').localeCompare(String(a[sortField] ?? '')));
    return list;
  }, [configs, searchTerm, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Facebook CAPI</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={handleAdd}><Plus size={13} /> Add Config <span className="adm-btn-badge">{configs.length}</span></button>
          <button className="adm-btn adm-btn-refresh" onClick={loadConfigs} disabled={loading}>{loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh</button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left"><span className="adm-count"><strong>{filtered.length}</strong> config(s)</span></div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>
      <div className="adm-table-card">
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('pixel_id')}>Pixel ID <SA field="pixel_id" /></th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('name')}>Name <SA field="name" /></th>
                <th className="adm-th">Access Token</th>
                <th className="adm-th">Mode</th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading...</td></tr>}
              {!loading && paginated.length === 0 && <tr><td colSpan={6} className="adm-empty">No CAPI configs found.</td></tr>}
              {!loading && paginated.map((c: CAPIConfig) => (
                <tr className="adm-row" key={c.id}>
                  <td className="adm-td"><span className="adm-mono">{c.pixel_id}</span></td>
                  <td className="adm-td"><span className="adm-name-main">{c.name}</span></td>
                  <td className="adm-td"><span className="adm-mono" style={{ color: '#888' }}>{c.access_token_masked || '—'}</span></td>
                  <td className="adm-td"><span className={'adm-chip ' + (c.test_event_code ? 'adm-chip-amber' : 'adm-chip-green')}>{c.test_event_code ? 'Testing' : 'Production'}</span></td>
                  <td className="adm-td"><span className={'adm-chip ' + (c.is_active ? 'adm-chip-green' : 'adm-chip-red')}>{c.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}</span></td>
                  <td className="adm-td" style={{ padding: '6px 4px' }}>
                    <div className="adm-actions-row">
                      <button className="adm-action-btn adm-ab-approve" style={{ fontSize: 10, padding: '0 6px', width: 'auto', minWidth: 36 }} onClick={() => handleTestConnection(c)} disabled={testingConnection === c.id} title="Test">{testingConnection === c.id ? <span className="adm-spin"><Loader2 size={10} /></span> : 'Test'}</button>
                      <button className="adm-action-btn adm-ab-edit" onClick={() => handleEdit(c)} title="Edit"><Pencil size={12} /></button>
                      <button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(c)} title="Delete"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="adm-pagination">
          <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
          <div className="adm-page-info">{filtered.length === 0 ? '0 configs' : ((currentPage - 1) * pageSize + 1) + '-' + Math.min(currentPage * pageSize, filtered.length) + ' of ' + filtered.length}</div>
          <div className="adm-page-controls">
            <button className="adm-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>&lsaquo;</button>
            <button className="adm-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>&rsaquo;</button>
          </div>
        </div>
      </div>
      <CAPIFormModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedConfig(null); }} onSave={handleSave} config={selectedConfig} />
      <ConfirmDialog isOpen={isDeleteDialogOpen} title="Delete CAPI Config" message={'Delete this configuration?'} onConfirm={handleDelete} onClose={() => setIsDeleteDialogOpen(false)} confirmText="Delete" cancelText="Cancel" type="danger" />
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default FacebookCAPIManagement;